import { ArrowLeft, PanelRight } from 'lucide-react';
import type { ReactNode } from 'react';
import type { ConversationDetail } from '@/shared/api';
import { awaitingReply, classificationLabel, waitingSince } from '../inboxModel';

const ICON_BUTTON =
  'border-line text-ink-soft hover:bg-card-alt hover:text-ink focus-visible:ring-sage flex size-11 flex-none items-center justify-center rounded-pill border focus-visible:outline-none focus-visible:ring-2';

function replyStatus(
  thread: ConversationDetail,
  now: Date,
  answeredVia: string | null,
): { text: string; open: boolean } {
  if (thread.isClosed) return { text: 'Geschlossen', open: false };
  // Über den Kanal kann auch eine geplante Nachricht geantwortet haben; das soll man sehen.
  if (!awaitingReply(thread)) {
    return { text: answeredVia ? `Beantwortet über ${answeredVia}` : 'Beantwortet', open: false };
  }
  const since = waitingSince(thread.lastMessageReceivedAt, now);
  return { text: since ? `Nicht beantwortet · ${since}` : 'Nicht beantwortet', open: true };
}

/** Kopf einer Unterhaltung: Gast, ob sie wartet, Aktionen, darunter das Kontext-Band. */
export function ConversationHeader({
  thread,
  name,
  answeredVia = null,
  actions,
  chips,
  onBack,
  onOpenContext,
}: {
  thread: ConversationDetail;
  name: string;
  /** Kanal, über den zuletzt direkt geantwortet wurde (nicht aus StayOn). */
  answeredVia?: string | null;
  actions?: ReactNode;
  chips: ReactNode;
  onBack?: () => void;
  onOpenContext?: () => void;
}) {
  const status = replyStatus(thread, new Date(), answeredVia);
  const tag = classificationLabel(thread.classification);
  return (
    <header className="border-hairline flex flex-none flex-col gap-2.5 border-b px-3 py-3 sm:px-5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Zurück zur Liste"
            className={ICON_BUTTON}
          >
            <ArrowLeft size={20} aria-hidden />
          </button>
        )}
        <div className="min-w-[12rem] flex-1">
          <h2 className="text-ink truncate font-serif text-[22px] font-bold tracking-[-0.02em]">
            {name}
          </h2>
          <p
            className={
              status.open ? 'text-sealwax text-[13px] font-bold' : 'text-ink-soft text-[13px]'
            }
          >
            {status.text}
            {tag ? ` · ${tag}` : ''}
          </p>
        </div>
        {onOpenContext && (
          <button
            type="button"
            onClick={onOpenContext}
            aria-label="Kontext zur Unterhaltung anzeigen"
            className={ICON_BUTTON}
          >
            <PanelRight size={17} aria-hidden />
          </button>
        )}
        {actions}
      </div>
      {chips}
    </header>
  );
}
